import React from 'react';
import {
  AssetRemoteAccessProfile,
  Flex,
  openNotificationWithIcon,
  Segment,
} from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Input from 'formik-antd/lib/input';
import Form from 'formik-antd/lib/form';
import SubmitButton from 'formik-antd/lib/submit-button';
import { Formik } from 'formik';
import Button from 'jsx/common/Button';
import { notification } from 'antd';
import Api from 'jsx/sessions/services/Api';
import { routes } from 'jsx/common/constants';
import classNames from 'classnames';

const requiredInputs: (t: TFunction) => any = (t: TFunction) => ({
  username: { key: 'input', value: t('Username') },
  domain: { key: 'input', value: t('Domain') },
  password: { key: 'password', value: t('Password') },
  passphrase: { key: 'password', value: t('Passphrase') },
  private_key: { key: 'textarea', value: t('Private key') },
});

export const Heading = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  return (
    <span
      className={classNames('text-sm pl-2 font-normal', className)}
      {...props}
    />
  );
};

const RemoteCreateConnection = ({
  profile,
  closeModal,
  requestId,
}: {
  profile: AssetRemoteAccessProfile;
  closeModal: () => void;
  requestId?: string;
}) => {
  const [t] = useTranslation(['remoteAccess', 'common']);

  const initialValues: AssetRemoteAccessProfile & any = {
    ...profile,
    profile_id: profile.id,
    remote_request_id: requestId,
  };
  profile.required_input_fields.forEach(x => (initialValues[x] = ''));
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async values => {
        delete values.required_input_fields;
        delete values.start_remote_session;
        delete values.create_remote_request;
        if (requestId) {
          delete values.profile_id;
        } else {
          delete values.remote_request_id;
        }
        let result = await Api.session.start(values);

        if (result.status === 201) {
          let data = await result.json();
          window.open(
            `/${routes.REMOTE_ACCESS}/client/${data.session_id}/`,
            // `/remote-session/${data.session_id}/`,
            '_blank'
          );
        } else if (result.status === 422) {
          openNotificationWithIcon(
            notification.error,
            t('Session could not be started'),
            t(
              'Your license has a limitation on the total active sessions and the maximum active sessions has been reached.'
            )
          );
        } else {
          openNotificationWithIcon(
            notification.error,
            t('Session could not be started'),
            t(
              'Due to technical issues the remote sessions could not be started. Please try again later.'
            )
          );
        }
        closeModal();
      }}
    >
      {formik => {
        const { values, errors } = formik;
        return (
          <Form>
            <Input type="hidden" name="id" value={values.id} />
            {profile.required_input_fields.length > 0 && (
              <Segment
                style={{ marginTop: 24 }}
                padding
                heading={
                  <Heading>
                    {t('Provide the required authentication information')}
                  </Heading>
                }
              >
                {profile.required_input_fields.map(input => {
                  const requiredInput = requiredInputs(t)[input];
                  const inputProps = {
                    name: input,
                    value: values[input],
                    style: { width: '100%' },
                  };
                  return (
                    <Flex className="mt-1" key={input}>
                      <span style={{ minWidth: 100, marginLeft: 8 }}>
                        {requiredInput.value}
                      </span>
                      {requiredInput.key === 'input' && (
                        <Input {...inputProps} />
                      )}
                      {requiredInput.key === 'textarea' && (
                        <Input.TextArea {...inputProps} rows={4} />
                      )}
                      {requiredInput.key === 'password' && (
                        <Input.Password {...inputProps} />
                      )}
                    </Flex>
                  );
                })}
              </Segment>
            )}
            <Flex
              v="center"
              h="end"
              className={profile.required_input_fields.length > 0 ? 'pr-6' : ''}
            >
              <Button
                variant="primary"
                ghost
                onClick={closeModal}
                data-ta="NewRequestCancelButton"
              >
                {t('Cancel')}
              </Button>
              <SubmitButton
                disabled={Object.keys(errors).length > 0}
                className="ml-2"
                data-ta="NewRequestConnectButton"
              >
                {t('Connect')}
              </SubmitButton>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RemoteCreateConnection;
